import { useState } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import { FiCopy, FiCheckCircle, FiUpload, FiAlertCircle } from 'react-icons/fi';
import './PowerPaymentForm.css';

// Plain axios instance - no JWT header, no auth:logout interceptor.
// This is intentional: /powerpayment is a public page, no login required.
const publicApi = axios.create({
  baseURL: process.env.REACT_APP_API_URL || 'https://paymentsapi.steponexp.com/api',
  timeout: 30000,
});

/* ---------- Pricing rules ----------
   ₹2500 per kV, per day.
   5kV and above automatically requires a 3-way power switch
   connection, which adds a flat ₹1000 surcharge for that day.
   18% GST is applied to the subtotal of all days.
------------------------------------- */
const PRICE_PER_KV = 2500;
const SURCHARGE_THRESHOLD_KV = 5;
const SURCHARGE_AMOUNT = 1000;
const GST_RATE = 0.18;

const CONTACT_PHONE = '+91 86570 22012';
const REPORTING_EMAIL = 'ayush@steponexp.com';
const ACCOUNTS_EMAIL = 'accounts@steponexp.com';

const GST_REGEX = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/;
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const PHONE_REGEX = /^[0-9]{10}$/;

// ── Bank details for direct transfer (replaces Razorpay to avoid the 3.7% gateway fee) ──
const BANK_DETAILS = {
  accountName: 'STEPONEXP LLP',
  bankName: 'ICICI BANK',
  accountNo: '160505001755',
  branchAddress: 'SHIBPUR BRANCH, 493C/A & 493C/B Gr Floor, GT Road South, Shibpur, Howrah - 711101',
  ifsc: 'ICIC0001605',
  accountType: 'Current Account',
  swift: 'ICICINBBCTS',
};

// ── UPI / GPay scan-to-pay details ── (QR image lives at /gpay-qr.png in public/)
const UPI_DETAILS = {
  upiId: '8655942332@okbizaxis',
  payeeName: 'Steponexp LLP',
  phone: '+91 86559 42332',
  qrImage: '/gpay-qr.png',
};

const MAX_RECEIPT_MB = 8;
const ACCEPTED_RECEIPT_TYPES = ['image/jpeg', 'image/png', 'image/webp', 'application/pdf'];

// ── Per-field validators — each returns an error string or null ────────────
const fieldValidators = {
  boothNo: (v) => (!v.trim() ? 'Booth number is required' : null),
  boothName: (v) => (!v.trim() ? 'Booth name is required' : null),
};

// Small helper — renders an error message below a field when present
function FieldError({ msg }) {
  if (!msg) return null;
  return (
    <span style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 11.5, fontWeight: 600, color: '#e74c3c', marginTop: 4 }}>
      <FiAlertCircle size={12} /> {msg}
    </span>
  );
}

function calcDay(kv) {
  const n = Number(kv) || 0;
  const base = n * PRICE_PER_KV;
  const surcharge = n >= SURCHARGE_THRESHOLD_KV ? SURCHARGE_AMOUNT : 0;
  return { kv: n, base, surcharge, total: base + surcharge };
}

function CopyField({ label, value }) {
  const handleCopy = () => {
    navigator.clipboard.writeText(value);
    toast.success(`${label} copied!`);
  };
  return (
    <div className="ppf-bank-row">
      <span className="ppf-bank-label">{label}</span>
      <span className="ppf-bank-value">{value}</span>
      <button type="button" className="ppf-result-copy-btn" onClick={handleCopy} aria-label={`Copy ${label}`}>
        <FiCopy />
      </button>
    </div>
  );
}

export default function PowerPaymentForm() {
  const [step, setStep] = useState(1); // 1 details, 2 power+summary, 3 pay+upload receipt, 4 confirmation

  const [eventName, setEventName] = useState('People Matters TechHR India 2026');

  const [boothNo, setBoothNo] = useState('');
  const [boothName, setBoothName] = useState('');
  const [errors, setErrors] = useState({});

  const [company, setCompany] = useState({
    name: '',
    address: '',
    gst: '',
    email: '',
    phone: '',
  });

  const [days, setDays] = useState([
    { date: '2026-08-05', kv: '' },
    { date: '2026-08-06', kv: '' },
    { date: '2026-08-07', kv: '' },
  ]);

  const [merchantTransactionId, setMerchantTransactionId] = useState(null);
  const [creatingBooking, setCreatingBooking] = useState(false);

  const [paymentMethod, setPaymentMethod] = useState('BANK_TRANSFER'); // 'BANK_TRANSFER' | 'UPI_QR'
  const [utrNumber, setUtrNumber] = useState('');
  const [receiptFile, setReceiptFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [confirmedResult, setConfirmedResult] = useState(null); // { bookingRef }

  // Validate a single field and update the errors object accordingly.
  const runFieldValidation = (key, value) => {
    const validator = fieldValidators[key];
    if (!validator) return;
    const msg = validator(value);
    setErrors((prev) => {
      const next = { ...prev };
      if (msg) next[key] = msg;
      else delete next[key];
      return next;
    });
  };

  const updateBoothNo = (value) => {
    value = value.replace(/[^a-zA-Z0-9-]/g, '');
    value = value.replace(/^[^a-zA-Z]+/, '');
    if (value.length > 0) {
      value = value.charAt(0).toUpperCase() + value.slice(1);
    }
    setBoothNo(value);
    runFieldValidation('boothNo', value);
  };

  const updateBoothName = (value) => {
    setBoothName(value);
    runFieldValidation('boothName', value);
  };

  // Adds a red border to an input when its field key has an active error
  const errStyle = (key) => errors[key] ? { borderColor: '#e74c3c', background: '#fff6f6' } : undefined;

  const updateCompany = (field, value) =>
    setCompany((c) => ({ ...c, [field]: value }));

  const updateDay = (idx, field, value) => {
    if (field === 'kv') {
      // Strip anything that isn't a digit (blocks decimals, minus, exponent notation)
      value = value.replace(/[^0-9]/g, '');
    }
    setDays((prev) => {
      const next = [...prev];
      next[idx] = { ...next[idx], [field]: value };
      return next;
    });
  };

  const validateStep1 = () => {
    const fieldErrors = {};
    const check = (key, value) => {
      const msg = fieldValidators[key]?.(value);
      if (msg) fieldErrors[key] = msg;
    };

    check('boothNo', boothNo);
    check('boothName', boothName);
    setErrors(fieldErrors);

    if (Object.keys(fieldErrors).length > 0) return 'Please fix the highlighted fields';

    if (!eventName.trim()) return 'Enter the event name';
    if (!company.name.trim()) return 'Enter the company name';
    if (!company.address.trim()) return 'Enter the company address';
    if (!GST_REGEX.test(company.gst.trim().toUpperCase()))
      return 'Enter a valid 15-character GST number';
    if (!EMAIL_REGEX.test(company.email.trim()))
      return 'Enter a valid email address';
    if (!PHONE_REGEX.test(company.phone.trim()))
      return 'Enter a valid 10-digit phone number';
    return null;
  };

  const validateStep2 = () => {
    for (let i = 0; i < days.length; i++) {
      const d = days[i];
      if (!d.date) return `Pick a date for Day ${i + 1}`;
      if (!d.kv || Number(d.kv) <= 0)
        return `Enter the power (kV) needed for Day ${i + 1}`;
    }
    return null;
  };

  const goToStep2 = () => {
    const err = validateStep1();
    if (err) return toast.error(err);
    setStep(2);
  };

  const dayCosts = days.map((d) => calcDay(d.kv));
  const subtotal = dayCosts.reduce((sum, d) => sum + d.total, 0);
  const gst = subtotal * GST_RATE;
  const grandTotal = Math.round((subtotal + gst) * 100) / 100; // 2 decimal places, no rounding up

  const fmtAmt = (n) => n.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  // ── Reserves a booking row (no gateway) so we have a merchant_transaction_id
  //    to attach the receipt/UTR to once the customer has paid ─────────────
  const goToPaymentStep = async () => {
    const err = validateStep2();
    if (err) return toast.error(err);

    setCreatingBooking(true);
    try {
      const dayBreakdown = days.map((d, i) => ({
        day: i + 1,
        date: d.date,
        kv: Number(d.kv),
        base: dayCosts[i].base,
        surcharge: dayCosts[i].surcharge,
        total: dayCosts[i].total,
      }));

      const payload = {
        name: company.name,
        phone: company.phone,
        email: company.email,
        amount: grandTotal,
        description: `Power connection - ${eventName} | Booth ${boothNo} (${boothName}) | ${dayBreakdown.map(d => `Day ${d.day} (${d.date}): ${d.kv}kV`).join(', ')} | GST 18% incl.`,
        eventName,
        boothNo,
        boothName,
        companyDetails: { name: company.name, address: company.address, gst: company.gst },
        dayBreakdown,
        subtotal,
        gstAmount: gst,
      };

      const { data } = await publicApi.post('/power/public/create-booking', payload);
      if (!data.success || !data.merchantTransactionId) {
        throw new Error(data.message || 'Could not start the booking');
      }

      setMerchantTransactionId(data.merchantTransactionId);
      setStep(3);
    } catch (e) {
      toast.error(e.response?.data?.message || e.message || 'Something went wrong');
    } finally {
      setCreatingBooking(false);
    }
  };

  const handleReceiptChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!ACCEPTED_RECEIPT_TYPES.includes(file.type)) {
      toast.error('Please upload a JPG, PNG, WEBP image or a PDF');
      e.target.value = '';
      return;
    }
    if (file.size > MAX_RECEIPT_MB * 1024 * 1024) {
      toast.error(`File is too large — max ${MAX_RECEIPT_MB}MB`);
      e.target.value = '';
      return;
    }
    setReceiptFile(file);
  };

  // ── Submits UTR + receipt screenshot/PDF after the customer has paid via
  //    bank transfer or by scanning the UPI/GPay QR. Backend emails
  //    accounts@steponexp.com with the receipt attached for manual verification ──
  const handleSubmitReceipt = async () => {
    if (!utrNumber.trim()) return toast.error('Enter the UTR / bank transaction reference number');
    if (!receiptFile) return toast.error('Attach a screenshot or PDF of the payment receipt');
    if (!merchantTransactionId) return toast.error('Booking reference missing — please go back and try again');

    setSubmitting(true);
    try {
      const formData = new FormData();
      formData.append('merchantTransactionId', merchantTransactionId);
      formData.append('utrNumber', utrNumber.trim());
      formData.append('paymentMethod', paymentMethod);
      formData.append('receipt', receiptFile);

      const { data } = await publicApi.post('/power/public/submit-receipt', formData);
      if (!data.success) {
        throw new Error(data.message || 'Could not submit your receipt');
      }

      setConfirmedResult({ bookingRef: merchantTransactionId });
      toast.success('Receipt submitted!');
      setStep(4);
    } catch (e) {
      toast.error(e.response?.data?.message || e.message || 'Something went wrong');
    } finally {
      setSubmitting(false);
    }
  };

  const startNewBooking = () => {
    setConfirmedResult(null);
    setMerchantTransactionId(null);
    setUtrNumber('');
    setReceiptFile(null);
    setPaymentMethod('BANK_TRANSFER');
    setStep(1);
    setBoothNo('');
    setBoothName('');
    setErrors({});
    setCompany({ name: '', address: '', gst: '', email: '', phone: '' });
    setDays([
      { date: '2026-08-05', kv: '' },
      { date: '2026-08-06', kv: '' },
      { date: '2026-08-07', kv: '' },
    ]);
  };

  return (
    <div className="ppf-page">
      <div className="ppf-shell">
        <header className="ppf-header">
          <div className="ppf-mark">
            <img src="/logo.svg" alt="SteponePay" style={{ width: '100%', height: '100%', padding: 8 }} />
          </div>
          <div>
            <h1>TechHR India Power Connection</h1>
            <p>Book day-wise power supply for your event</p>
          </div>
          <div className="ppf-steps">
            <span className={step === 1 ? 'active' : 'done'}>1. Details</span>
            <span className="ppf-step-divider" />
            <span className={step === 2 ? 'active' : step > 2 ? 'done' : ''}>2. Power</span>
            <span className="ppf-step-divider" />
            <span className={step === 3 ? 'active' : step > 3 ? 'done' : ''}>3. Pay & upload receipt</span>
            <span className="ppf-step-divider" />
            <span className={step === 4 ? 'active' : ''}>4. Confirmation</span>
          </div>
        </header>

        {step === 1 && (
          <section className="ppf-card">
            <h2>Event & company details</h2>

            <label className="ppf-field">
              <span>Event name</span>
              <input type="text" value={eventName} placeholder="e.g. StepOne" disabled />
            </label>

            <div className="ppf-grid">
              <label className="ppf-field">
                <span>Booth No.</span>
                <input
                  type="text"
                  value={boothNo}
                  onChange={(e) => updateBoothNo(e.target.value)}
                  placeholder="e.g. A-14"
                  style={errStyle('boothNo')}
                />
                <FieldError msg={errors.boothNo} />
              </label>

              <label className="ppf-field">
                <span>Booth Name</span>
                <input
                  type="text"
                  value={boothName}
                  onChange={(e) => updateBoothName(e.target.value)}
                  placeholder="e.g. StepOne Experiential Zone"
                  style={errStyle('boothName')}
                />
                <FieldError msg={errors.boothName} />
              </label>
            </div>

            <div className="ppf-divider">Company details</div>

            <div className="ppf-grid">
              <label className="ppf-field">
                <span>Company Name</span>
                <input
                  type="text"
                  value={company.name}
                  onChange={(e) => updateCompany('name', e.target.value)}
                  placeholder="e.g. StepOne"
                />
              </label>

              <label className="ppf-field">
                <span>GST Number</span>
                <input
                  type="text"
                  value={company.gst}
                  onChange={(e) => updateCompany('gst', e.target.value.toUpperCase())}
                  placeholder="XXXXXXXXXXXXXXX"
                  maxLength={15}
                />
              </label>

              <label className="ppf-field ppf-span-2">
                <span>Address</span>
                <input
                  type="text"
                  value={company.address}
                  onChange={(e) => updateCompany('address', e.target.value)}
                  placeholder="Registered address"
                />
              </label>

              <label className="ppf-field">
                <span>Email</span>
                <input
                  type="email"
                  value={company.email}
                  onChange={(e) => updateCompany('email', e.target.value)}
                  placeholder="company@email.com"
                />
              </label>

              <label className="ppf-field">
                <span>Phone</span>
                <input
                  type="tel"
                  value={company.phone}
                  onChange={(e) => updateCompany('phone', e.target.value)}
                  placeholder="10-digit mobile number"
                  maxLength={10}
                />
              </label>
            </div>

            <div className="ppf-contact-box">
              <div>
                <span className="ppf-contact-label">Support</span>
                <span>{CONTACT_PHONE}</span>
              </div>
              <div>
                <span className="ppf-contact-label">Reporting</span>
                <span>{REPORTING_EMAIL}</span>
              </div>
            </div>

            <button className="ppf-btn-primary" onClick={goToStep2}>
              Continue To Power Consumption Selection
            </button>
          </section>
        )}

        {step === 2 && (
          <section className="ppf-card">
            <h2>Power selection</h2>

            <p className="ppf-note-text">
              Note: Power is charged at ₹2,500 per kV per day. For 5 kV and above, a 3-way power
              connection is mandatory and attracts an additional ₹1,000 charge per day. GST extra
              as applicable.
            </p>

            <div className="ppf-day-list">
              {days.map((d, i) => {
                const cost = dayCosts[i];
                const over = cost.kv >= SURCHARGE_THRESHOLD_KV;
                return (
                  <div className="ppf-day-row" key={i}>
                    <div className="ppf-day-label">Day {i + 1}</div>

                    <label className="ppf-field">
                      <span>Date</span>
                      <input type="date" value={d.date} disabled />
                    </label>

                    <label className="ppf-field">
                      <span>Power (kV)</span>
                      <input
                        type="number"
                        min="1"
                        step="1"
                        inputMode="numeric"
                        value={d.kv}
                        onKeyDown={(e) => {
                          if (['.', 'e', 'E', '-', '+'].includes(e.key)) {
                            e.preventDefault();
                          }
                        }}
                        onChange={(e) => updateDay(i, 'kv', e.target.value)}
                        onPaste={(e) => {
                          const pasted = e.clipboardData.getData('text');
                          if (!/^[0-9]+$/.test(pasted)) e.preventDefault();
                        }}
                        placeholder="e.g. 5"
                      />
                    </label>

                    <div className="ppf-day-meter">
                      <div
                        className="ppf-day-meter-fill"
                        style={{ width: `${(Math.min(cost.kv, 20) / 20) * 100}%` }}
                      />
                    </div>

                    <div className="ppf-day-cost">
                      <span>₹{cost.total.toLocaleString('en-IN')}</span>
                      {over && (
                        <span className="ppf-surcharge-tag">
                          incl. 3-way switch +₹{SURCHARGE_AMOUNT.toLocaleString('en-IN')}
                        </span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="ppf-summary">
              <div className="ppf-summary-row">
                <span>Subtotal</span>
                <span>₹{fmtAmt(subtotal)}</span>
              </div>
              <div className="ppf-summary-row">
                <span>GST (18%)</span>
                <span>₹{fmtAmt(gst)}</span>
              </div>
              <div className="ppf-summary-row ppf-summary-total">
                <span>Total amount</span>
                <span>₹{fmtAmt(grandTotal)}</span>
              </div>
            </div>

            <div className="ppf-actions">
              <button className="ppf-btn-secondary" onClick={() => setStep(1)}>
                Back
              </button>
              <button className="ppf-btn-primary" onClick={goToPaymentStep} disabled={creatingBooking}>
                {creatingBooking ? 'Preparing…' : `Continue to Pay ₹${fmtAmt(grandTotal)}`}
              </button>
            </div>
          </section>
        )}

        {step === 3 && (
          <section className="ppf-card">
            <h2>Pay by bank transfer </h2>
            {/* or UPI */}

            <p className="ppf-note-text">
              Amount to pay: <strong>₹{fmtAmt(grandTotal)}</strong>. Choose either option below, complete
              the payment for this exact amount, then upload your receipt and reference number so our
              accounts team can verify it.
            </p>

            {/* ── Payment method toggle ── */}
            <div className="ppf-method-toggle">
              <button
                type="button"
                className={`ppf-method-btn ${paymentMethod === 'BANK_TRANSFER' ? 'active' : ''}`}
                onClick={() => setPaymentMethod('BANK_TRANSFER')}
              >
                Bank Transfer (NEFT/RTGS/IMPS)
              </button>
              {/* <button
                type="button"
                className={`ppf-method-btn ${paymentMethod === 'UPI_QR' ? 'active' : ''}`}
                onClick={() => setPaymentMethod('UPI_QR')}
              >
                Scan & Pay (GPay / PhonePe / Paytm / UPI)
              </button> */}
            </div>

            {paymentMethod === 'BANK_TRANSFER' ? (
              <div className="ppf-bank-box">
                <CopyField label="Account Name" value={BANK_DETAILS.accountName} />
                <CopyField label="Bank Name" value={BANK_DETAILS.bankName} />
                <CopyField label="Account No." value={BANK_DETAILS.accountNo} />
                <CopyField label="IFSC Code" value={BANK_DETAILS.ifsc} />
                <CopyField label="Account Type" value={BANK_DETAILS.accountType} />
                <CopyField label="SWIFT" value={BANK_DETAILS.swift} />
                <div className="ppf-bank-row ppf-bank-row--address">
                  <span className="ppf-bank-label">Branch Address</span>
                  <span className="ppf-bank-value">{BANK_DETAILS.branchAddress}</span>
                </div>
              </div>
            ) : (
              <div className="ppf-upi-box">
                <img src={UPI_DETAILS.qrImage} alt="Scan to pay via UPI" className="ppf-upi-qr" />
                <div className="ppf-upi-details">
                  <CopyField label="UPI ID" value={UPI_DETAILS.upiId} />
                  <CopyField label="Payee Name" value={UPI_DETAILS.payeeName} />
                  <p className="ppf-note-text" style={{ marginTop: 8 }}>
                    Scan this QR with any UPI app (Google Pay, PhonePe, Paytm, BHIM) and manually
                    enter <strong>₹{fmtAmt(grandTotal)}</strong> as the amount before paying.
                  </p>
                </div>
              </div>
            )}

            <div className="ppf-divider">Upload proof of payment</div>

            <label className="ppf-field">
              <span>UTR / Bank </span>
              {/* or UPI transaction reference number */}
              <input
                type="text"
                value={utrNumber}
                onChange={(e) => setUtrNumber(e.target.value)}
                placeholder="Enter the reference number from your bank/UPI app"
              />
            </label>

            <label className="ppf-field">
              <span>Payment receipt (screenshot or PDF, max {MAX_RECEIPT_MB}MB)</span>
              <div className="ppf-upload-box">
                <input
                  type="file"
                  accept="image/jpeg,image/png,image/webp,application/pdf"
                  onChange={handleReceiptChange}
                  id="ppf-receipt-input"
                  style={{ display: 'none' }}
                />
                <label htmlFor="ppf-receipt-input" className="ppf-btn-secondary" style={{ cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                  <FiUpload /> {receiptFile ? 'Change file' : 'Choose file'}
                </label>
                {receiptFile && <span className="ppf-upload-filename">{receiptFile.name}</span>}
              </div>
            </label>

            <div className="ppf-actions">
              <button className="ppf-btn-secondary" onClick={() => setStep(2)}>
                Back
              </button>
              <button className="ppf-btn-primary" onClick={handleSubmitReceipt} disabled={submitting}>
                {submitting ? 'Submitting…' : 'Submit Payment Details'}
              </button>
            </div>
          </section>
        )}

        {/* ── Step 4: Confirmation - receipt submitted, accounts team notified by email ── */}
        {step === 4 && confirmedResult && (
          <section className="ppf-card">

            {/* ── Compact summary strip ── */}
            <div className="ppf-s3-summary">
              {/* Company pill */}
              <div className="ppf-s3-col">
                <span className="ppf-s3-label">Company</span>
                <span className="ppf-s3-value">{company.name}</span>
                {company.gst && <span className="ppf-s3-sub">{company.gst}</span>}
                <span className="ppf-s3-sub">Booth {boothNo} — {boothName}</span>
              </div>
              <div className="ppf-s3-divider" />
              {/* Consumption pills - one per day */}
              <div className="ppf-s3-col">
                <span className="ppf-s3-label">Power consumption</span>
                <div className="ppf-s3-days">
                  {days.map((d, i) => {
                    const cost = dayCosts[i];
                    return (
                      <span key={i} className={`ppf-s3-day-pill ${cost.kv >= SURCHARGE_THRESHOLD_KV ? 'ppf-s3-day-pill--3way' : ''}`}>
                        Day {i + 1}: {d.kv} kV - ₹{fmtAmt(cost.total)}
                        {cost.kv >= SURCHARGE_THRESHOLD_KV && <span className="ppf-s3-3way">3-way</span>}
                      </span>
                    );
                  })}
                </div>
              </div>
              <div className="ppf-s3-divider" />
              {/* Totals */}
              <div className="ppf-s3-col ppf-s3-col--totals">
                <div className="ppf-s3-total-row"><span>Subtotal</span><span>₹{fmtAmt(subtotal)}</span></div>
                <div className="ppf-s3-total-row"><span>GST 18%</span><span>₹{fmtAmt(gst)}</span></div>
                <div className="ppf-s3-total-row ppf-s3-total-row--grand"><span>Total</span><span>₹{fmtAmt(grandTotal)}</span></div>
              </div>
            </div>

            {/* Success banner */}
            <div className="ppf-result-banner">
              <FiCheckCircle size={22} />
              <div>
                <div className="ppf-result-banner-title">Payment receipt received!</div>
                <div className="ppf-result-banner-sub">
                  Your payment proof has been sent to our accounts team. Your power connection booking
                  will be confirmed once the payment is verified against our bank statement.
                </div>
              </div>
            </div>

            {/* Amount */}
            <div className="ppf-result-amount-box">
              <span className="ppf-result-amount-label">Amount to be received (incl. GST)</span>
              <span className="ppf-result-amount-value">₹{fmtAmt(grandTotal)}</span>
            </div>

            {/* Reference details */}
            <div className="ppf-result-txn-row">
              <span>Booking reference:</span>
              <span className="ppf-mono">{confirmedResult.bookingRef}</span>
              <button
                className="ppf-result-copy-btn"
                onClick={() => { navigator.clipboard.writeText(confirmedResult.bookingRef); toast.success('Reference copied!'); }}
              >
                Copy
              </button>
            </div>
            <div className="ppf-result-txn-row">
              <span>UTR / Transaction ref:</span>
              <span className="ppf-mono">{utrNumber}</span>
              <button
                className="ppf-result-copy-btn"
                onClick={() => { navigator.clipboard.writeText(utrNumber); toast.success('UTR copied!'); }}
              >
                Copy
              </button>
            </div>

            <p className="ppf-result-note">
              A copy of your receipt has been emailed to <strong>{ACCOUNTS_EMAIL}</strong> for
              verification. You can safely close this tab - the booking has been saved. For any
              queries, reach us at {CONTACT_PHONE} or {REPORTING_EMAIL}.
            </p>

            <button className="ppf-link-reset" onClick={startNewBooking}>
              ← Start a new booking
            </button>
          </section>
        )}
      </div>
    </div>
  );
}